import React, { useState } from "react";
import { api } from "../lib/api";
import { PageHeader } from "../components/common";
import { Button } from "../components/ui/button";
import TrustBadge from "../components/TrustBadge";
import { toast } from "sonner";
import { GitCompare, Loader2, Plus, Minus, RefreshCw } from "lucide-react";

export default function CompareAgreementsPage() {
  const [v1, setV1] = useState("");
  const [v2, setV2] = useState("");
  const [v1Label, setV1Label] = useState("Version 1");
  const [v2Label, setV2Label] = useState("Version 2");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/agreements/compare", {
        v1, v2, v1_label: v1Label || "Version 1", v2_label: v2Label || "Version 2",
      });
      setResult(data);
      toast.success("Compared.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Compare failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="compare-page">
      <PageHeader
        eyebrow="La Confrontation"
        title="Version compare"
        description="Paste two versions of the same agreement. Marianne calls out what was added, removed and changed — with the impact of each change."
      />

      <div className="grid md:grid-cols-2 gap-4 mb-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <input
              value={v1Label}
              onChange={(e) => setV1Label(e.target.value)}
              className="text-sm border-b border-[#E8E3F0] bg-transparent focus:outline-none focus:border-[#7B56C9] flex-1"
              data-testid="v1-label"
            />
          </div>
          <textarea
            value={v1}
            onChange={(e) => setV1(e.target.value)}
            rows={16}
            placeholder="Paste the earlier version…"
            className="w-full border border-[#E8E3F0] px-3 py-2 text-sm font-mono resize-y bg-white"
            data-testid="v1-input"
          />
        </div>
        <div>
          <div className="flex items-center gap-2 mb-2">
            <input
              value={v2Label}
              onChange={(e) => setV2Label(e.target.value)}
              className="text-sm border-b border-[#E8E3F0] bg-transparent focus:outline-none focus:border-[#7B56C9] flex-1"
              data-testid="v2-label"
            />
          </div>
          <textarea
            value={v2}
            onChange={(e) => setV2(e.target.value)}
            rows={16}
            placeholder="Paste the newer version…"
            className="w-full border border-[#E8E3F0] px-3 py-2 text-sm font-mono resize-y bg-white"
            data-testid="v2-input"
          />
        </div>
      </div>

      <Button
        onClick={run}
        disabled={busy || v1.trim().length < 30 || v2.trim().length < 30}
        className="rounded-none bg-[#2A0F5A] hover:bg-[#3D1F85] text-[#FAFAF8] mb-8"
        data-testid="compare-btn"
      >
        {busy ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Comparing…</> : <><GitCompare size={14} className="mr-1.5" /> Compare versions</>}
      </Button>

      {result && (
        <div className="space-y-6" data-testid="compare-result">
          <TrustBadge trust={result.trust} />

          {result.summary && (
            <div className="border-l-2 border-[#7B56C9] bg-white p-4">
              <div className="label-eyebrow text-[#C5A059] mb-2">Summary</div>
              <div className="text-sm">{result.summary}</div>
            </div>
          )}

          <Panel title="Added" count={result.added.length} icon={Plus} tone="added" testid="compare-added">
            {result.added.map((c, i) => (
              <Change key={i} c={c} showV2 />
            ))}
          </Panel>

          <Panel title="Removed" count={result.removed.length} icon={Minus} tone="removed" testid="compare-removed">
            {result.removed.map((c, i) => (
              <Change key={i} c={c} showV1 />
            ))}
          </Panel>

          <Panel title="Changed" count={result.changed.length} icon={RefreshCw} tone="changed" testid="compare-changed">
            {result.changed.map((c, i) => (
              <Change key={i} c={c} showV1 showV2 />
            ))}
          </Panel>
        </div>
      )}
    </div>
  );
}

function Panel({ title, count, icon: Icon, tone, children, testid }) {
  const stripe = tone === "added" ? "border-l-emerald-600" : tone === "removed" ? "border-l-red-700" : "border-l-[#C5A059]";
  const iconColor = tone === "added" ? "text-emerald-700" : tone === "removed" ? "text-red-700" : "text-[#8a6b1f]";
  return (
    <div className={`border border-[#E8E3F0] border-l-2 ${stripe} bg-white p-5`} data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <Icon size={14} className={iconColor} />
        <div className="label-eyebrow">{title}</div>
        <span className="text-xs text-[#4A5568]">({count})</span>
      </div>
      {count === 0 ? (
        <div className="text-sm text-[#4A5568] italic">None.</div>
      ) : (
        <div className="space-y-4">{children}</div>
      )}
    </div>
  );
}

function Change({ c, showV1, showV2 }) {
  return (
    <div>
      <div className="font-medium text-sm mb-1">{c.heading}</div>
      {c.impact && <div className="text-xs text-[#4A5568] mb-2">{c.impact}</div>}
      {showV1 && c.v1_excerpt && (
        <div className="border-l-2 border-red-700 bg-red-50 px-3 py-2 text-xs italic mb-1">
          <span className="label-eyebrow !text-[10px] text-red-800 mr-2">Before</span>"{c.v1_excerpt}"
        </div>
      )}
      {showV2 && c.v2_excerpt && (
        <div className="border-l-2 border-emerald-600 bg-emerald-50 px-3 py-2 text-xs italic">
          <span className="label-eyebrow !text-[10px] text-emerald-800 mr-2">After</span>"{c.v2_excerpt}"
        </div>
      )}
    </div>
  );
}
